import sanitizeHtml from "sanitize-html";

/**
 * Sanitises HTML emitted by the Visual CMS card-description editor before it
 * ships to the public site.
 *
 * The card-description editor (CardDescriptionEditor) is locked to four
 * formatting primitives — bold, italic, link, plain paragraph + hard break —
 * so the allow-list here is intentionally tiny. Anything outside this list
 * (scripts, iframes, on* handlers, style attributes) is stripped.
 *
 * Uses `sanitize-html` (the package already in use by
 * `src/lib/sanitize/cms-html.ts`) rather than DOMPurify so we share one
 * sanitiser dependency across the codebase.
 */

const CARD_ALLOWED_TAGS = ["p", "br", "strong", "b", "em", "i", "a"];

/**
 * Detects whether a string already contains HTML markup. Used so legacy
 * plain-text descriptions (saved before the rich editor existed) can keep
 * rendering as-is, while new HTML descriptions go through the sanitiser.
 */
export function isHtmlString(value: string | null | undefined): boolean {
  if (!value) return false;
  return /<[a-z][\s\S]*>/i.test(value);
}

/**
 * Sanitises a card description for safe rendering with
 * `dangerouslySetInnerHTML`. Forces external links to open in a new tab
 * with `rel="noopener noreferrer"` because admins typically link to docs
 * or sister pages.
 */
export function sanitizeCardDescription(value: string | null | undefined): string {
  if (!value) return "";
  return sanitizeHtml(value, {
    allowedTags: CARD_ALLOWED_TAGS,
    allowedAttributes: {
      // `class` is whitelisted here so the brand-green class that
      // `transformTags` below stamps onto every <a> survives sanitisation.
      a: ["href", "target", "rel", "title", "class"],
    },
    allowedSchemes: ["http", "https", "mailto", "tel"],
    allowProtocolRelative: false,
    transformTags: {
      // Harden every <a>: open in a new tab + prevent reverse-tab-nabbing,
      // and force the brand-green link styling so hyperlinks are visually
      // distinct from surrounding copy on the public site. The class is
      // applied here (not in the editor) so legacy links and links from
      // any future input source render consistently.
      a: (tagName, attribs) => ({
        tagName,
        attribs: {
          ...attribs,
          target: attribs.target || "_blank",
          rel: attribs.rel || "noopener noreferrer",
          class: "text-v2-green-600 underline font-medium hover:text-v2-green-700 transition-colors",
        },
      }),
    },
  });
}

/**
 * Strips all HTML to readable plain text.
 *
 * For fields the rich-text editor wraps in markup (e.g. `<p>…</p>`) but that
 * render in a PLAIN-TEXT slot — hero taglines / subtitles — so the raw tags
 * never show up literally on the page. Block and `<br>` boundaries become
 * spaces so adjacent paragraphs aren't glued together, and the common HTML
 * entities are decoded back to characters for clean display.
 */
export function stripHtmlToText(value: string | null | undefined): string {
  if (!value) return "";
  if (!isHtmlString(value)) return value.trim();

  const spaced = value
    .replace(/<\s*br\s*\/?>/gi, " ")
    .replace(/<\/\s*(p|div|h[1-6]|li)\s*>/gi, " ");
  const stripped = sanitizeHtml(spaced, { allowedTags: [], allowedAttributes: {} });

  return stripped
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&apos;/g, "'")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Splits a card description into per-line HTML chunks for renderers (like
 * ResponsibilitySection) that previously split on `\n` to build per-item
 * lists. Works for both legacy plain-text content (split by newline) and
 * new HTML content (split by paragraph boundary or `<br>`).
 *
 * Each returned chunk is sanitised inline HTML — the wrapping `<p>` is
 * dropped so callers can wrap each chunk in their own list-item / span /
 * etc.
 */
export function splitCardDescriptionLines(value: string | null | undefined): string[] {
  if (!value) return [];
  const trimmed = value.trim();
  if (!trimmed) return [];

  if (!isHtmlString(trimmed)) {
    // Legacy plain-text path — split by newline as before.
    return trimmed
      .split(/\r?\n/)
      .map((s) => s.trim())
      .filter(Boolean);
  }

  // Run sanitisation first so we never split untrusted markup.
  const safe = sanitizeCardDescription(trimmed);

  // Replace `<br>` boundaries with paragraph boundaries, then split on
  // paragraph close tags.
  const flattened = safe.replace(/<br\s*\/?>/gi, "</p><p>");
  return flattened
    .split(/<\/p>/i)
    .map((chunk) => chunk.replace(/^<p[^>]*>/i, "").trim())
    .filter(Boolean);
}
